import { Check, ArrowUpRight } from "lucide-react";
import { useMagnetic } from "@/components/effects";

function TierCard({ t }) {
  const magRef = useMagnetic(t.accent ? 0.28 : 0);
  return (
    <div
      data-testid={`pricing-tier-${t.name.toLowerCase()}`}
      className={`relative bg-[#050505] p-8 md:p-10 flex flex-col ${t.accent ? "ring-1 ring-yellow-400/40 lg:-my-4" : ""}`}
    >
      {t.badge && (
        <div className="absolute top-0 right-0 bg-yellow-400 text-black px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em]">
          {t.badge}
        </div>
      )}

      <div className="flex items-center gap-3 mb-6">
        <span className={`w-2 h-2 ${t.accent ? "bg-yellow-400" : "bg-zinc-600"}`} />
        <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
          {t.name}
        </span>
      </div>

      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-heading text-5xl font-light tracking-tighter text-white">
          {t.price}
          <span className="text-yellow-400 ml-0.5">*</span>
        </span>
        <span className="text-sm text-zinc-500 font-mono">{t.cadence}</span>
      </div>
      <p className="text-sm text-zinc-400 font-light leading-relaxed mb-8 min-h-[44px]">
        {t.summary}
      </p>

      <ul className="space-y-3 mb-10 flex-1">
        {t.features.map((f) => (
          <li key={f} className="flex items-start gap-3 text-sm text-zinc-300 font-light leading-relaxed">
            <Check className={`w-4 h-4 mt-0.5 shrink-0 ${t.accent ? "text-yellow-400" : "text-zinc-500"}`} strokeWidth={2} />
            {f}
          </li>
        ))}
      </ul>

      <a
        ref={magRef}
        href={`mailto:Robert@InvisibleSystem.Design?subject=${encodeURIComponent(t.subject)}&body=${encodeURIComponent("Hey Robert! I'm so excited by this. When can we talk?")}`}
        data-testid={`pricing-cta-${t.name.toLowerCase()}`}
        className={`group inline-flex items-center justify-between gap-2 px-5 py-3.5 text-xs uppercase tracking-[0.22em] font-medium ${
          t.accent
            ? "bg-yellow-400 text-black hover:bg-yellow-500"
            : "border border-white/20 text-white hover:bg-white/5 hover:border-white/40 transition-colors"
        }`}
      >
        {t.cta}
        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2} />
      </a>
    </div>
  );
}

const TIERS = [
  {
    name: "Light",
    price: "$500",
    cadence: "/month",
    summary: "For founders validating a single conversational flow, or maintaining an app with minimal changes.",
    features: [
      "Unlimited change requests",
      "1 messaging platform",
      "48-hour turnaround",
      "Shared infrastructure",
      "Email support",
    ],
    cta: "Begin Light",
    subject: "Interested in the Light Plan",
    accent: false,
  },
  {
    name: "Standard",
    price: "$1,500",
    cadence: "/month",
    summary: "For teams shipping steadily.",
    features: [
      "Unlimited change requests",
      "Up to 3 platforms (iMessage, WhatsApp, Telegram, Slack)",
      "Priority email turnaround (24 hours)",
      "Dedicated engineer in the loop",
      "Quarterly architecture review",
      "Incident escalation",
    ],
    cta: "Choose Standard",
    subject: "Interested in the Standard Plan",
    accent: true,
    badge: "Most Common",
  },
  {
    name: "Priority",
    price: "$3,000",
    cadence: "/month",
    summary: "For companies where the conversation IS the product.",
    features: [
      "Unlimited change requests",
      "All 4 platforms, unified",
      "6-hour turnaround for critical paths",
      "Dedicated systems architect",
      "Custom SLA & security review",
      "Direct line for incidents",
    ],
    cta: "Speak to an Architect",
    subject: "Interested in the Priority Plan",
    accent: false,
  },
];

export default function Pricing() {
  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="relative py-24 md:py-32 border-b border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex items-center gap-3 mb-12">
          <span className="text-xs uppercase tracking-[0.22em] text-zinc-500 font-mono">
            §07 · Plans
          </span>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-end mb-16">
          <h2 className="lg:col-span-8 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light leading-snug">
            One subscription.
            <br />
            <span className="text-zinc-500">The whole engineering team, invisible.</span>
          </h2>
          <p className="lg:col-span-4 text-base leading-relaxed text-zinc-400 font-light">
            Flat monthly pricing. No SOWs, no surprise invoices. Pause or change
            tier any month — your system keeps running.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {TIERS.map((t) => (
            <TierCard key={t.name} t={t} />
          ))}
        </div>

        <p className="mt-8 text-xs font-mono text-zinc-500 max-w-xl">
          <span className="text-yellow-400">*</span> Does not include iMessage carrier plans.
        </p>

        <p className="mt-3 text-xs font-mono text-zinc-500 max-w-xl">
          &quot;Within reason&quot; means we'll never nickel-and-dime you, but we
          reserve the right to talk if scope changes shape entirely. Custom
          enterprise terms available.
        </p>
      </div>
    </section>
  );
}
